import { useState, useEffect } from 'react'
import {
  Controller,
  Control,
  ControllerRenderProps,
  FieldErrors,
  useWatch,
} from 'react-hook-form'
import { Icon, IconType } from '../../../../components'
import { Brand } from '../../../../gql/types'
import { mapCurrencyToSign } from '../../../../utils/mappers'

interface PriceInputProps {
  field: ControllerRenderProps<any, 'customCreatorEarningsCents'>
  currencySign: string
  minimumDollars: number
  error?: string
}

const PriceInput = ({
  field,
  currencySign,
  minimumDollars,
  error,
}: PriceInputProps) => {
  const [inputValue, setInputValue] = useState<string>(
    field.value != null ? Math.round(field.value / 100).toString() : '',
  )

  useEffect(() => {
    if (field.value != null) {
      setInputValue(Math.round(field.value / 100).toString())
    }
  }, [field.value])

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '')
    setInputValue(value)

    if (value === '') {
      field.onChange(null)
      return
    }

    const dollars = parseInt(value, 10)
    if (!isNaN(dollars)) {
      field.onChange(dollars * 100)
    }
  }

  return (
    <>
      <div className='relative rounded-md shadow-sm max-w-xs'>
        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
          <span className='text-gray-500 sm:text-sm'>{currencySign}</span>
        </div>
        <input
          type='text'
          inputMode='numeric'
          name='customCreatorPrice'
          id='customCreatorPrice'
          className={`block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 ring-1 ring-inset ${
            error
              ? 'ring-red-500 focus:ring-red-500'
              : 'ring-gray-300 focus:ring-blue-600'
          } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
          placeholder={minimumDollars.toString()}
          value={inputValue}
          onChange={handlePriceChange}
        />
      </div>
      <p className='mt-1 text-xs text-gray-500'>
        Minimum payout: {currencySign}
        {minimumDollars}
      </p>
    </>
  )
}

interface Props {
  brand: Brand
  quantity: number
  setQuantity: (quantity: number) => void
  control: Control<any>
  errors: FieldErrors
}

const SetCustomCreatorPrice = ({
  brand,
  quantity,
  setQuantity,
  control,
  errors,
}: Props) => {
  const minimumCents = brand?.minimumCreatorPayoutCents ?? 15000
  const minimumDollars = minimumCents / 100
  const error = errors.customCreatorEarningsCents?.message as string
  const customCreatorEarningsCents = useWatch({
    control,
    name: 'customCreatorEarningsCents',
  })
  const pricePerCreator =
    customCreatorEarningsCents != null ? customCreatorEarningsCents / 100 : 0
  const totalCampaignPrice = pricePerCreator * quantity

  return (
    <div>
      <div className='mt-10 sm:mt-0'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <div>
              <h3 className='text-lg font-semibold font-normal font-sans leading-6 text-gray-900'>
                Videos & Creator Price
              </h3>
              <p className='mt-1 text-sm font-normal font-sans leading-5 text-gray-500'>
                Set how many videos you need and the price each creator will be
                paid per collaboration.
              </p>
            </div>
          </div>
          <div className='mt-5 md:col-span-2 md:mt-0'>
            <div className='md:shadow rounded-t-md sm:overflow-hidden'>
              <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Number of videos
                  </label>
                  <div className='flex flex-row justify-between'>
                    <div className='isolate inline-flex rounded-md'>
                      <button
                        type='button'
                        className='relative inline-flex items-center gap-x-1.5 rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10'
                        onClick={() => setQuantity(Math.max(quantity - 1, 1))}
                      >
                        <Icon
                          type={IconType.minus}
                          className='-ml-0.5 h-5 w-5 text-gray-500 text-[20px]'
                        />
                      </button>
                      <button
                        type='button'
                        className='relative -ml-px inline-flex items-center bg-white px-3 py-2 text-md font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10'
                      >
                        {quantity}
                      </button>
                      <button
                        type='button'
                        className='relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10'
                        onClick={() => setQuantity(Math.min(quantity + 1, 100))}
                      >
                        <Icon
                          type={IconType.plus}
                          className='-ml-0.5 h-5 w-5 text-gray-500 text-[20px]'
                        />
                      </button>
                    </div>
                    {totalCampaignPrice > 0 && (
                      <p className='flex items-baseline justify-end gap-x-2'>
                        <span className='text-5xl font-bold tracking-tight text-gray-900'>
                          {mapCurrencyToSign(brand?.currency)}{' '}
                          {totalCampaignPrice}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor='customCreatorPrice'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Creator price per collaboration
                  </label>
                  <Controller
                    name='customCreatorEarningsCents'
                    control={control}
                    rules={{
                      required: 'Creator price is required.',
                      validate: (value) => {
                        if (value == null || value <= 0)
                          return 'Creator price must be greater than 0.'
                        if (value < minimumCents)
                          return `Minimum creator payout is $${minimumDollars}.`
                        if (value > 100000)
                          return 'Creator price cannot exceed $1,000.'
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <PriceInput
                        field={field}
                        currencySign={mapCurrencyToSign(brand?.currency)}
                        minimumDollars={minimumDollars}
                        error={error}
                      />
                    )}
                  />
                  {error && (
                    <p className='mt-2 text-sm text-red-600'>{error}</p>
                  )}
                </div>
                {totalCampaignPrice > 0 && (
                  <div className='mt-2 text-gray-700 text-sm'>
                    {mapCurrencyToSign(brand?.currency)}
                    {pricePerCreator} per creator × {quantity} video
                    {quantity > 1 ? 's' : ''} ={' '}
                    {mapCurrencyToSign(brand?.currency)}
                    {totalCampaignPrice} total campaign payout.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='hidden sm:block' aria-hidden='true'>
        <div className='py-5'>
          <div className='border-t border-gray-200' />
        </div>
      </div>
    </div>
  )
}

export { SetCustomCreatorPrice }
